/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 *
 *
 * @author Julia Ignatieva
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Ilya Baranchikov
 * @version 1.27.0
 * @since 1.14.0
 */

import { useCallback, useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { icons } from '@assets/index';
import { LinkBlock, TextBlockModal, TextBlockStatus } from '@components/common';
import { ImgStyled } from '@components/common/Block/Img';
import { ETextBlockStatus } from '@components/common/TextBlockStatus/textBlockStatusTypes';
import type { ISimpleTableColumns, ISimpleTableData } from '@components/SimpleTable/SimpleTable';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardChangeLock,
} from '@modules/internationalization/intSelectors';
import type ICommonTranslation from '@modules/internationalization/types/common';
import type { IWizardChangeLockTranslation } from '@modules/internationalization/types/wizards/changeLock';
import { AccessComponentKeyEnum } from '@modules/role/constants/guard/guardConstants';
import { makeSelectGuardComponentEnabled } from '@modules/role/selectors/guardSelector';
import { WizardStepConfirmationRemovedItemStyled } from '@modules/wizard/components/styled/WizardConfirmationStyled';
import WizardConfirmationStep from '@modules/wizard/components/WizardConfirmationStep';
import type { IWizardSummaryParam } from '@modules/wizard/components/WizardSummary/WizardSummary';
import { wizardReportStorageSaveAction } from '@modules/wizard/wizardActions';
import type { IRootReducer } from '@reducers/rootReducer';
import { CLIENT_ID, RoutePath } from '@routes/constants/routesConstants';

import type { TWizardChangeLockSetParamsPayload } from '../types/wizardChangeLockActionTypes';
import { wizardChangeLockSetParamsAction, wizardChangeLockStartValidateAction } from '../wizardChangeLockActions';
import { EWizardChangeLockFieldList, EWizardChangeLockSteps } from '../wizardChangeLockConstant';
import type { IWizardChangeLockOrderSubscriber, IWizardChangeLockReducer } from '../wizardChangeLockReducer';
import {
    makeSelectWizardChangeLockDateFrom,
    makeSelectWizardChangeLockDateTo,
    makeSelectWizardChangeLockIsOnlineCheckProceeded,
    makeSelectWizardChangeLockIsUnlockSubscribers,
    makeSelectWizardChangeLockOrderCheckResult,
    makeSelectWizardChangeLockRemovedSubscribers,
} from '../wizardChangeLockSelectors';

const WizardChangeLockConfirmation = (props: IConnectedProps & IDispatchProps) => {
    const {
        dateFrom,
        dateTo,
        isOnlineCheckProceeded,
        isUnlockSubscribers,
        isWizardChangeLockHideCostEnabled,
        orderCheckResult,
        removedSubscribers,
        translation,
        translationCommon,

        saveCurrentStepState,
        setParams,
        startValidate,
        storageSave,
    } = props;

    const stepTranslations = translation[EWizardChangeLockSteps.StepConfirmation];

    const [tableData, setTableData] = useState<ISimpleTableData[]>([]);
    const [tableColumns, setTableColumns] = useState<ISimpleTableColumns[]>([]);
    const [summaryParams, setSummaryParams] = useState<IWizardSummaryParam[]>([]);
    const [subsCount, setSubsCount] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);

    const removeHandler = useCallback(
        (id: number) => {
            setParams({
                removedSubscribers: removedSubscribers.concat(id),
            });
        },
        [setParams, removedSubscribers],
    );

    const recoverHandler = useCallback(
        (id: number) => {
            setParams({
                removedSubscribers: [...removedSubscribers.filter((item) => item !== id)],
            });
        },
        [setParams, removedSubscribers],
    );

    const getItemStatus = useCallback(
        (item: IWizardChangeLockOrderSubscriber) => {
            return (
                <TextBlockModal disabled={item.status === ETextBlockStatus.OK} text={item.errorMessage}>
                    {/* @ts-ignore */}
                    <TextBlockStatus status={item.status}>
                        <LinkBlock disabled={item.status === ETextBlockStatus.OK}>
                            {isUnlockSubscribers
                                ? stepTranslations.unlockStatus[item.status]
                                : stepTranslations.lockStatus[item.status]}
                        </LinkBlock>
                    </TextBlockStatus>
                </TextBlockModal>
            );
        },
        [isUnlockSubscribers, stepTranslations],
    );

    /**
     * Preparing summary params
     */
    useEffect(() => {
        setSummaryParams([
            {
                title: isUnlockSubscribers
                    ? translation.wizardSummary.unlockSubscribersAmount
                    : translation.wizardSummary.lockSubscribersAmount,
                values: [`${subsCount}`],
            },
            ...(isOnlineCheckProceeded && !isWizardChangeLockHideCostEnabled
                ? [
                      {
                          title: isUnlockSubscribers
                              ? translation.wizardSummary.unlockTotalCost
                              : translation.wizardSummary.lockTotalCost,
                          values: [`${totalPrice} ${translationCommon.currency}`],
                      },
                  ]
                : []),
            {
                title: isUnlockSubscribers
                    ? translation.wizardSummary.unlockDateFrom
                    : translation.wizardSummary.lockDateFrom,
                values: [dateFrom],
            },
            {
                hide: isUnlockSubscribers,
                title: translation.wizardSummary.lockDateTo,
                values: [dateTo],
            },
        ]);
    }, [
        dateFrom,
        dateTo,
        isOnlineCheckProceeded,
        isUnlockSubscribers,
        isWizardChangeLockHideCostEnabled,
        subsCount,
        totalPrice,
        translation.wizardSummary,
        translationCommon.currency,
    ]);

    /**
     * Preparing table columns
     */
    useEffect(() => {
        setTableColumns([
            {
                id: EWizardChangeLockFieldList.MSISDN,
                title: stepTranslations.subscriberChangeLockTableTitles[EWizardChangeLockFieldList.MSISDN],
                width: 140,
            },
            ...(isOnlineCheckProceeded && !isWizardChangeLockHideCostEnabled
                ? [
                      {
                          id: EWizardChangeLockFieldList.COST,
                          title: stepTranslations.subscriberChangeLockTableTitles[EWizardChangeLockFieldList.COST],
                          width: 120,
                      },
                  ]
                : []),
            ...(isOnlineCheckProceeded
                ? [
                      {
                          id: EWizardChangeLockFieldList.STATUS,
                          title: stepTranslations.subscriberChangeLockTableTitles[EWizardChangeLockFieldList.STATUS],
                      },
                  ]
                : []),
            {
                id: EWizardChangeLockFieldList.DELETE,
                title: '',
                align: 'right',
                width: 40,
            },
        ]);
    }, [isOnlineCheckProceeded, isWizardChangeLockHideCostEnabled, stepTranslations.subscriberChangeLockTableTitles]);

    /**
     * Preparing table data
     */
    useEffect(() => {
        setTableData(
            orderCheckResult.orderSubs.map((item: IWizardChangeLockOrderSubscriber) => ({
                id: item.subsId,
                msisdn: (
                    <LinkBlock url={RoutePath.subscriberId.replace(CLIENT_ID, item.subsId.toString())}>
                        {item.msisdn}
                    </LinkBlock>
                ),
                ...(isOnlineCheckProceeded && !isWizardChangeLockHideCostEnabled
                    ? {
                          cost: (
                              <div>
                                  {item.charge || 0} {translationCommon.currency}
                              </div>
                          ),
                      }
                    : {}),
                ...(isOnlineCheckProceeded ? { status: getItemStatus(item) } : {}),
                delete: (
                    <div>
                        {removedSubscribers.includes(item.subsId) && (
                            <WizardStepConfirmationRemovedItemStyled style={{ position: 'absolute', left: 0, top: 0 }}>
                                <span>{stepTranslations.removedFromList}</span>
                                <LinkBlock onClick={() => recoverHandler(item.subsId)}>
                                    {stepTranslations.recoveryToList}
                                </LinkBlock>
                            </WizardStepConfirmationRemovedItemStyled>
                        )}
                        <ImgStyled
                            src={icons.removeSmallOpacityIcon}
                            width="24px"
                            onClick={() => removeHandler(item.subsId)}
                        />
                    </div>
                ),
            })),
        );
    }, [
        getItemStatus,
        isOnlineCheckProceeded,
        isWizardChangeLockHideCostEnabled,
        orderCheckResult,
        recoverHandler,
        removedSubscribers,
        removeHandler,
        stepTranslations,
        translationCommon,
    ]);

    /**
     * Calc count and totalCost
     */
    useEffect(() => {
        const listFiltered = orderCheckResult.orderSubs.filter((item) => !removedSubscribers.includes(item.subsId));
        setSubsCount(listFiltered.length);
        saveCurrentStepState({
            subscriberIds: listFiltered.map((item) => item.subsId),
        });
        setTotalPrice(
            listFiltered.reduce((acc, cur) => {
                return acc + (cur.charge || 0);
            }, 0),
        );
        startValidate();
    }, [orderCheckResult, removedSubscribers, saveCurrentStepState, startValidate]);

    return (
        <WizardConfirmationStep
            isOnlineCheckProceeded={isOnlineCheckProceeded}
            isReportSubscribers={false}
            onlineCheckInfo={stepTranslations.onlineCheck}
            startValidate={startValidate}
            storageSave={storageSave}
            summaryParams={summaryParams}
            summaryTitle=""
            tableColumns={tableColumns}
            tableData={tableData}
            wizardReport={translation.wizardReport}
        />
    );
};

export interface IConnectedProps
    extends Pick<
        IWizardChangeLockReducer,
        | 'dateFrom'
        | 'dateTo'
        | 'isOnlineCheckProceeded'
        | 'isUnlockSubscribers'
        | 'orderCheckResult'
        | 'removedSubscribers'
    > {
    isWizardChangeLockHideCostEnabled: boolean;
    translation: IWizardChangeLockTranslation;
    translationCommon: ICommonTranslation;
}

interface IDispatchProps {
    saveCurrentStepState: (payload: TWizardChangeLockSetParamsPayload) => void;
    setParams: (payload: TWizardChangeLockSetParamsPayload) => void;
    startValidate: () => void;
    storageSave: () => void;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    dateFrom: makeSelectWizardChangeLockDateFrom(),
    dateTo: makeSelectWizardChangeLockDateTo(),
    isOnlineCheckProceeded: makeSelectWizardChangeLockIsOnlineCheckProceeded(),
    isUnlockSubscribers: makeSelectWizardChangeLockIsUnlockSubscribers(),
    isWizardChangeLockHideCostEnabled: makeSelectGuardComponentEnabled(AccessComponentKeyEnum.wizardChangeLockHideCost),
    orderCheckResult: makeSelectWizardChangeLockOrderCheckResult(),
    removedSubscribers: makeSelectWizardChangeLockRemovedSubscribers(),
    translation: makeSelectTranslationWizardChangeLock(),
    translationCommon: makeSelectTranslationCommon(),
});

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    saveCurrentStepState: (payload: TWizardChangeLockSetParamsPayload) =>
        dispatch(wizardChangeLockSetParamsAction(payload)),
    setParams: (payload) => dispatch(wizardChangeLockSetParamsAction(payload)),
    startValidate: () => dispatch(wizardChangeLockStartValidateAction()),
    storageSave: () => dispatch(wizardReportStorageSaveAction()),
});

export default connect(mapStateToProps, mapDispatchToProps)(WizardChangeLockConfirmation);
